<template>
  <div class="flex items-center space-x-2">
    <div
      v-if="$props.tradeItem.has_children"
      class="flex items-center w-full"
      :style="{ paddingLeft: `${($props.depth * 20) + 16}px` }"
    >
      <button
        type="button"
        class="btn-icon btn-icon-default"
        @click="showChildren = !showChildren"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-transform duration-200"
          :class="[{'rotate-90': showChildren}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <p
        class="w-80 text-wrap cursor-pointer ml-2"
        @click="showChildren = !showChildren"
      >
        {{ $props.tradeItem.bill_item }}) {{ $props.tradeItem.name }}
      </p>

      <input
        v-if="selectableDescendants.length > 0"
        type="checkbox"
        class="form-checkbox ml-auto"
        :checked="parentSelected"
        :disabled="allDescendantsAssignedToPackage"
        @click.stop="onParentCheckboxClick"
      >
    </div>

    <label
      v-else
      class="flex items-center w-full justify-between"
      :style="{ paddingLeft: `${($props.depth * 20) + 16}px` }"
    >
      <div class="flex items-center space-x-2">
        <button
          type="button"
          class="btn-icon btn-icon-default invisible"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-transform duration-200">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <p class="w-80 text-wrap">
          {{ $props.tradeItem.bill_item }}) {{ $props.tradeItem.name }}
        </p>
      </div>

      <input
        v-model="tradeItemSelected"
        type="checkbox"
        :disabled="!!$props.packageTradeItem"
        class="form-checkbox"
      >
    </label>

    <AppInfoHover :offset="[0, 5]">
      <template #icon>
        <div
          class="w-4 h-4 rounded-full"
          :class="[tradeItem.package_trade_items.length === 0 ? 'bg-red-300' : 'bg-green-400']"
        />
      </template>

      <template #default>
        <p
          v-if="tradeItem.package_trade_items.length === 0"
          class="text-sm"
        >
          {{ $t('Subcontract.PackagesDashboard.TradeItemSelector.status.unassigned') }}
        </p>

        <div
          v-else
          class="text-sm font-medium max-w-60"
        >
          {{ $t('Subcontract.PackagesDashboard.TradeItemSelector.status.assignedToPackages') }}

          <ul class="font-normal">
            <li
              v-for="packageTradeItem in tradeItem.package_trade_items"
              :key="packageTradeItem.id"
              class="list-disc ml-5"
            >
              <p>
                {{ packageTradeItem.package.name }}
              </p>
            </li>
          </ul>
        </div>
      </template>
    </AppInfoHover>
  </div>

  <template v-if="showChildren">
    <TradeItemSelector
      v-for="tradeItem in tradeItems"
      :key="tradeItem.id"
      :trade-item="tradeItem"
      :package-trade-item="tradeItem.package_trade_items.find((packageTradeItem) => packageTradeItem.package_id === $props.package.id)"
      :is-selected="selectedTradeItems.findIndex((selectedTradeItem) => selectedTradeItem.id === tradeItem.id) >= 0"
      :selected-trade-items="selectedTradeItems"
      :package="$props.package"
      :depth="depth ? depth + 1 : 1"
      @select-trade-item="$emit('selectTradeItem', $event?.tradeItem ? { tradeItem: $event.tradeItem} : { tradeItem: tradeItem })"
      @deselect-trade-item="$emit('deselectTradeItem', $event?.tradeItem ? { tradeItem: $event.tradeItem} : { tradeItem: tradeItem })"
    />
  </template>
</template>

<script setup lang="ts">
import Package from '@/models/Package';
import PackageTradeItem from '@/models/PackageTradeItem';
import TradeItem from '@/models/TradeItem';
import { useTradeItemsStore } from '@/store/tradeItems';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  tradeItem: TradeItem;
  packageTradeItem?: PackageTradeItem;
  isSelected: boolean;
  selectedTradeItems: TradeItem[];
  package: Package;
  depth?: number;
}>();

const emits = defineEmits<{
  (e: 'selectTradeItem', data?: { tradeItem: TradeItem });
  (e: 'deselectTradeItem', data?: { tradeItem: TradeItem });
}>();

const tradeItemSelected = ref(!!props.packageTradeItem);

watch(() => props.isSelected, (newValue) => {
  if(!props.packageTradeItem) {
    tradeItemSelected.value = newValue;
  }
}, { immediate: true });

watch(tradeItemSelected, (newValue) => {
  if(props.packageTradeItem) {
    return;
  }

  if(newValue) {
    emits('selectTradeItem');
  } else {
    emits('deselectTradeItem');
  }
});

function collectSelectableLeaves(tradeItem: TradeItem): TradeItem[] {
  const tradeItemsStore = useTradeItemsStore();

  if(tradeItem.has_children) {
    return tradeItemsStore.models
      .where('parent_id', tradeItem.id)
      .with('package_trade_items')
      .where((item) => item.budget > 0 || item.has_children)
      .orderBy('sort_order')
      .get()
      .flatMap(collectSelectableLeaves);
  }

  return tradeItem.budget > 0 ? [tradeItem] : [];
}

function getSelectableLeafItemsForParent(tradeItem: TradeItem): TradeItem[] {
  return collectSelectableLeaves(tradeItem);
}

function isTradeItemAssignedToPackage(tradeItem: TradeItem, packageId: Id): boolean {
  return tradeItem.package_trade_items.some((packageTradeItem) => packageTradeItem.package_id === packageId);
}

const tradeItemsStore = useTradeItemsStore();

const selectableDescendants = computed(() => {
  return getSelectableLeafItemsForParent(props.tradeItem);
});

const descendantsAssignedToPackage = computed(() => {
  return selectableDescendants.value.filter((tradeItem) => {
    return isTradeItemAssignedToPackage(tradeItem, props.package.id);
  }).length;
});

const descendantsInSelection = computed(() => {
  return selectableDescendants.value.filter((tradeItem) => {
    return props.selectedTradeItems.some((selectedTradeItem) => selectedTradeItem.id === tradeItem.id);
  }).length;
});

const descendantsSelectedOrAssigned = computed(() => {
  return descendantsAssignedToPackage.value + descendantsInSelection.value;
});

const allDescendantsAssignedToPackage = computed(() => {
  return selectableDescendants.value.length > 0 &&
    descendantsAssignedToPackage.value === selectableDescendants.value.length;
});

const parentSelected = computed(() => {
  return selectableDescendants.value.length > 0 &&
    descendantsSelectedOrAssigned.value === selectableDescendants.value.length;
});

const selectAllDescendants = (tradeItem: TradeItem) => {
  if(tradeItem.has_children) {
    const childItems = tradeItemsStore.models.with('package_trade_items', (query) => {
      query.with('package');
    }).where('parent_id', tradeItem.id)
      .where((item) => item.budget > 0 || item.has_children)
      .get();

    childItems.forEach((childItem) => {
      selectAllDescendants(childItem);
    });
  } else if(
    !isTradeItemAssignedToPackage(tradeItem, props.package.id) && tradeItem.budget > 0
  ) {
    emits('selectTradeItem', { tradeItem });
  }
};

const deselectAllDescendants = () => {
  selectableDescendants.value.forEach((tradeItem) => {
    if(props.selectedTradeItems.some((selectedTradeItem) => selectedTradeItem.id === tradeItem.id)) {
      emits('deselectTradeItem', { tradeItem });
    }
  });
};

const onParentCheckboxClick = () => {
  if(parentSelected.value) {
    deselectAllDescendants();
  } else {
    selectAllDescendants(props.tradeItem);
  }
};

const tradeItems = computed(() => {
  return tradeItemsStore.models
    .where('parent_id', props.tradeItem.id)
    .with('package_trade_items', (query) => {
      query.with('package');
    })
    .where((tradeItem) => tradeItem.budget > 0 || tradeItem.has_children)
    .orderBy('sort_order')
    .get();
});

const showChildren = ref(false);
</script>

<style scoped></style>
